import React, { useState, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Button, Text, VStack, HStack, Box, Alert, AlertIcon, FormControl, FormLabel, Select, Textarea,
    NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
    Badge, Flex, Tooltip, useToast,
} from '@chakra-ui/react';
import { cancelarItemVenda, desfazerCancelamentoItemVenda } from '../../services/api';
import { useConfirm } from '../ConfirmDialog';
import ReembolsoMercadoPagoModal from '../ReembolsoMercadoPagoModal';
import { formatBRL } from '../../utils/formatters';

export const MOTIVOS_CANCELAMENTO = [
    { valor: 'furo_estoque', rotulo: 'Furo de estoque' },
    { valor: 'desistencia', rotulo: 'Desistência do cliente' },
    { valor: 'defeito', rotulo: 'Defeito' },
    { valor: 'troca', rotulo: 'Troca' },
    { valor: 'erro_pedido', rotulo: 'Erro no pedido' },
    { valor: 'outro', rotulo: 'Outro' },
];

export const rotuloMotivo = (valor) => MOTIVOS_CANCELAMENTO.find((m) => m.valor === valor)?.rotulo || valor || '';

const LISTA_ATIVA = ['na_lista', 'respondido', 'aprovado', 'parcialmente_aprovado'];

export const resumoItem = (produto) => {
    const quantidade = Number(produto?.quantidade) || 0;
    const cancelada = Number(produto?.quantidade_cancelada) || 0;
    const devolvida = Number(produto?.quantidade_devolvida) || 0;
    return { quantidade, cancelada, devolvida, restante: Math.max(0, quantidade - cancelada - devolvida) };
};

export const itemForaDoPedido = (produto) => {
    const r = resumoItem(produto);
    return r.cancelada + r.devolvida > 0 && r.restante === 0;
};

export const produtosAtivos = (pedido) => (pedido?.produtos || []).filter((p) => !itemForaDoPedido(p));

export const podeItemSerCancelado = (pedido, produto, podeAgir) =>
    !!podeAgir && !!pedido?.pode_cancelar_item && resumoItem(produto).restante > 0 && !!produto?.item_venda_id;

const ehSite = (pedido) => String(pedido?.origem || '').toUpperCase() === 'ECOMMERCE';

const dataBr = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' }) : '');

const CancelarItemModal = ({ alvo, onClose, onConcluido }) => {
    const toast = useToast();
    const [quantidade, setQuantidade] = useState(1);
    const [motivo, setMotivo] = useState('');
    const [observacao, setObservacao] = useState('');
    const [enviando, setEnviando] = useState(false);

    const produto = alvo?.produto;
    const pedido = alvo?.pedido;
    const r = resumoItem(produto);
    const qtdValida = Number.isInteger(quantidade) && quantidade >= 1 && quantidade <= r.restante;
    const lista = produto?.lista_compra_status;
    const naLista = lista && LISTA_ATIVA.includes(lista.status);

    const fechar = () => {
        if (enviando) return;
        setQuantidade(1);
        setMotivo('');
        setObservacao('');
        onClose();
    };

    const confirmar = async () => {
        setEnviando(true);
        try {
            const resp = await cancelarItemVenda(produto.item_venda_id, { quantidade, motivo, observacao: observacao.trim() || null });
            if (resp?.success) {
                toast({ title: resp.message || 'Item cancelado.', status: 'success', duration: 5000, isClosable: true });
                setQuantidade(1);
                setMotivo('');
                setObservacao('');
                onClose();
                onConcluido && onConcluido();
            } else {
                toast({ title: 'Item não cancelado', description: resp?.error || 'Erro desconhecido.', status: 'error', duration: 9000, isClosable: true });
            }
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal isOpen={!!alvo} onClose={fechar} size="md" isCentered>
            <ModalOverlay />
            <ModalContent mx={4}>
                <ModalHeader fontSize="md">Cancelar item</ModalHeader>
                <ModalCloseButton isDisabled={enviando} />
                <ModalBody>
                    {produto && (
                        <VStack align="stretch" spacing={3}>
                            <Box bg="gray.50" p={3} borderRadius="md" borderWidth="1px">
                                <Text fontSize="sm" fontWeight="600" wordBreak="break-word">{produto.nome}</Text>
                                <Text fontSize="xs" color="gray.600" mt={1}>
                                    SKU {produto.id} · Vendidas {r.quantidade}
                                    {r.cancelada > 0 ? ` · Canceladas ${r.cancelada}` : ''}
                                    {r.devolvida > 0 ? ` · Devolvidas ${r.devolvida}` : ''}
                                </Text>
                            </Box>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" mb={1}>Quantidade a cancelar</FormLabel>
                                <NumberInput size="sm" min={1} max={r.restante} value={quantidade} isDisabled={enviando || r.restante <= 1}
                                    onChange={(_, n) => setQuantidade(Number.isNaN(n) ? 0 : n)} keepWithinRange clampValueOnBlur>
                                    <NumberInputField inputMode="numeric" />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                                <Text fontSize="xs" color={qtdValida ? 'gray.500' : 'red.500'} mt={1}>
                                    De 1 a {r.restante}
                                </Text>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" mb={1}>Motivo</FormLabel>
                                <Select size="sm" placeholder="Selecione" value={motivo} onChange={(e) => setMotivo(e.target.value)} isDisabled={enviando}>
                                    {MOTIVOS_CANCELAMENTO.map((m) => <option key={m.valor} value={m.valor}>{m.rotulo}</option>)}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm" mb={1}>Observação</FormLabel>
                                <Textarea size="sm" rows={2} maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} isDisabled={enviando} />
                            </FormControl>

                            {naLista && (
                                <Alert status="warning" borderRadius="md" fontSize="sm">
                                    <AlertIcon />
                                    Este item já está na lista de compras{lista.lista_id ? ` #${lista.lista_id}` : ''}. Avise o fornecedor.
                                </Alert>
                            )}
                            {ehSite(pedido) && (
                                <Alert status="info" borderRadius="md" fontSize="sm">
                                    <AlertIcon />
                                    O dinheiro não é devolvido agora. O reembolso é feito pelo selo do item.
                                </Alert>
                            )}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={fechar} isDisabled={enviando}>Voltar</Button>
                    <Button colorScheme="red" onClick={confirmar} isLoading={enviando} loadingText="Cancelando..."
                        isDisabled={!qtdValida || !motivo}>
                        Cancelar {qtdValida ? `${quantidade} un.` : 'item'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export const useAcoesItemVenda = ({ onAlterado } = {}) => {
    const toast = useToast();
    const { confirm, ConfirmPortal } = useConfirm();
    const [alvoCancelar, setAlvoCancelar] = useState(null);
    const [ocorrenciaReembolso, setOcorrenciaReembolso] = useState(null);

    const abrirCancelar = useCallback((pedido, produto) => setAlvoCancelar({ pedido, produto }), []);
    const abrirReembolso = useCallback((ocorrencia) => setOcorrenciaReembolso(ocorrencia), []);

    const desfazer = useCallback(async (ocorrencia) => {
        const estoque = Number(ocorrencia.estoque_devolvido) || 0;
        const ok = await confirm({
            title: 'Desfazer cancelamento',
            body: `${ocorrencia.quantidade} unidade(s) voltam para a venda.${estoque > 0 ? ` ${estoque} unidade(s) saem do estoque de novo.` : ''}`,
            confirmText: 'Desfazer',
            cancelText: 'Voltar',
            confirmColor: 'red',
        });
        if (!ok) return;
        const resp = await desfazerCancelamentoItemVenda(ocorrencia.id);
        if (resp?.success) {
            toast({ title: resp.message || 'Cancelamento desfeito.', status: 'success', duration: 5000, isClosable: true });
            onAlterado && onAlterado();
        } else {
            toast({ title: 'Não foi possível desfazer', description: resp?.error || 'Erro desconhecido.', status: 'error', duration: 9000, isClosable: true });
        }
    }, [confirm, onAlterado, toast]);

    const modais = (
        <>
            <CancelarItemModal alvo={alvoCancelar} onClose={() => setAlvoCancelar(null)} onConcluido={onAlterado} />
            {ocorrenciaReembolso && (
                <ReembolsoMercadoPagoModal
                    isOpen={!!ocorrenciaReembolso}
                    onClose={() => setOcorrenciaReembolso(null)}
                    registroId={ocorrenciaReembolso.id}
                    recurso="vendas/ocorrencias"
                    onSuccess={onAlterado}
                />
            )}
            <ConfirmPortal />
        </>
    );

    return { abrirCancelar, abrirReembolso, desfazer, modais };
};

const SeloPadrao = ({ scheme, onClick, children }) => (
    <Badge colorScheme={scheme} fontSize="9px" alignSelf="flex-start" whiteSpace="normal" textAlign="left"
        cursor={onClick ? 'pointer' : 'default'} onClick={onClick} _hover={onClick ? { opacity: 0.8 } : undefined}
        as={onClick ? 'button' : 'span'} type={onClick ? 'button' : undefined}>
        {children}
    </Badge>
);

export const SelosOcorrenciasItem = ({ pedido, produto, acoes, podeAgir, Selo = SeloPadrao, desativarDica = false, mostrarCancelar = true }) => {
    const ocorrencias = Array.isArray(produto?.ocorrencias) ? produto.ocorrencias : [];
    const r = resumoItem(produto);
    const podeCancelar = mostrarCancelar && podeItemSerCancelado(pedido, produto, podeAgir);

    if (ocorrencias.length === 0 && !podeCancelar) return null;

    return (
        <Flex wrap="wrap" gap={1} mt={1} align="center">
            {ocorrencias.map((o) => {
                const cancelamento = o.tipo === 'cancelamento';
                const total = o.quantidade >= r.quantidade;
                const dica = [o.usuario && `Por ${o.usuario}`, o.data && `em ${dataBr(o.data)}`, o.observacao].filter(Boolean).join(' · ');
                const podeDesfazer = podeAgir && cancelamento && o.origem === 'manual' && !o.reembolso && !!pedido?.pode_cancelar_item;
                return (
                    <React.Fragment key={o.id}>
                        <Tooltip label={dica} isDisabled={desativarDica || !dica} openDelay={300}>
                            <Box as="span" display="inline-flex">
                                <Selo tipo={o.tipo} forte={total} scheme={cancelamento ? 'red' : 'purple'}>
                                    {cancelamento ? 'Cancelado' : 'Devolvido'}{total ? '' : ` ${o.quantidade} de ${r.quantidade}`}{o.motivo ? ` · ${rotuloMotivo(o.motivo)}` : ''}
                                </Selo>
                            </Box>
                        </Tooltip>
                        {cancelamento && ehSite(pedido) && (
                            <Selo tipo={o.reembolso ? 'reembolsado' : 'reembolso'} scheme={o.reembolso ? 'green' : 'orange'} onClick={podeAgir ? () => acoes.abrirReembolso(o) : undefined}>
                                {o.reembolso ? `Reembolsado ${formatBRL(o.reembolso.valor)}` : 'Reembolso pendente'}
                            </Selo>
                        )}
                        {o.estoque_pendente > 0 && (
                            <Selo tipo="estoque" scheme="orange">{o.estoque_pendente} un. conferida(s): dar entrada manual no estoque</Selo>
                        )}
                        {podeDesfazer && (
                            <Button size="xs" variant="link" color="gray.700" fontWeight="600" fontSize="10px" onClick={() => acoes.desfazer(o)}>Desfazer</Button>
                        )}
                    </React.Fragment>
                );
            })}
            {podeCancelar && (
                <HStack spacing={1}>
                    <Button size="xs" variant="link" color="red.600" fontWeight="600" fontSize="10px" onClick={() => acoes.abrirCancelar(pedido, produto)}>
                        Cancelar item
                    </Button>
                </HStack>
            )}
        </Flex>
    );
};

export const LinhasExtrato = ({ extrato, Linha }) => {
    if (!extrato) return null;
    const linhas = [
        ['Valor bruto dos itens', extrato.valor_bruto, false],
        extrato.cancelados > 0 && ['(-) Itens cancelados', extrato.cancelados, true],
        extrato.devolvidos > 0 && ['(-) Itens devolvidos', extrato.devolvidos, true],
        extrato.descontos > 0 && ['(-) Descontos', extrato.descontos, true],
        extrato.acrescimos > 0 && ['(+) Acréscimos', extrato.acrescimos, false],
        extrato.outros_reembolsos > 0 && ['(-) Outros reembolsos', extrato.outros_reembolsos, true],
        extrato.frete > 0 && ['Frete', extrato.frete, false],
    ].filter(Boolean);
    return linhas.map(([rotulo, valor, negativo]) => <Linha key={rotulo} rotulo={rotulo} valor={valor} negativo={negativo} />);
};
